import { BatchStageBadge } from '@/components/common/BatchStageBadge';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { CODE_BADGE_CLASS } from '@/styles/codeBadge';
import { formatDisplayDate, formatTime } from '@/utils/date';
import { formatNumber, pluralize } from '@/utils/number';
import { DONATION_BATCH_STAGE_LABEL } from '../constants/marketPromotion.constants';
import type { DonationBatch, DonationBatchMember } from '../types/marketPromotionP2.types';
import { getBatchNumber } from '../utils/batchNumber';
import { RecordSummaryPanel, type SummaryField } from './RecordSummaryPanel';

const DETAIL_LINE_CLASS = 'block text-xs font-normal text-muted-foreground';

function formatCovering(batch: DonationBatch): string {
  if (batch.trnDateFrom === null) return EMPTY_VALUE;
  return `${formatDisplayDate(batch.trnDateFrom)} – ${formatDisplayDate(batch.trnDateTo)}`;
}

const BATCH_SUMMARY_FIELDS: readonly SummaryField<DonationBatch>[] = [
  { label: 'Donation Item', value: (batch) => batch.itemName },
  { label: 'Covering Work', value: formatCovering },
  {
    label: 'Published',
    value: (batch) =>
      batch.publishDate ? (
        <>
          <span className="block">{formatDisplayDate(batch.publishDate)}</span>
          <span className={DETAIL_LINE_CLASS}>{formatTime(batch.publishDate)}</span>
        </>
      ) : null,
  },
];

function getBatchBadges(batch: DonationBatch) {
  return (
    <>
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        Batch
        <span className={CODE_BADGE_CLASS}>{`#${String(getBatchNumber(batch))}`}</span>
      </span>
      <BatchStageBadge stage={batch.stage} stageLabels={DONATION_BATCH_STAGE_LABEL} />
    </>
  );
}

function getMembersNote(members: DonationBatchMember[] | undefined): string | null {
  if (members === undefined) return null;
  const donationCount = members.reduce((total, member) => total + member.donationCount, 0);
  return `${formatNumber(members.length)} ${pluralize(members.length, 'ZM')} · ${formatNumber(donationCount)} ${pluralize(donationCount, 'donation')}`;
}

interface DonationBatchSummaryPanelProps {
  batch: DonationBatch | undefined;
  members: DonationBatchMember[] | undefined;
}

export function DonationBatchSummaryPanel({ batch, members }: DonationBatchSummaryPanelProps) {
  return (
    <RecordSummaryPanel
      ariaLabel="Batch summary"
      record={batch}
      badges={getBatchBadges}
      title={(record) => record.name ?? `Batch #${String(getBatchNumber(record))}`}
      amountLabel="Batch Amount"
      amount={(record) => Math.round(record.promoItemQty)}
      amountNote={() => getMembersNote(members)}
      fields={BATCH_SUMMARY_FIELDS}
    />
  );
}
